import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { retryQuery } from "@/lib/retryQuery";
import { Skeleton } from "@/components/ui/skeleton";

interface ScoreEntry {
  id: string;
  score: number;
  base_score: number;
  social_boost: number;
  rating: string | null;
  calculated_at: string;
}

/**
 * Inline TOSS score history popover, keyed by token symbol.
 * Shows the last 8 recorded scores from `toss_score_history`, with older
 * values struck through when they differ from the current.
 */
export const ScoreHistoryInline = ({ symbol }: { symbol: string }) => {
  const [entries, setEntries] = useState<ScoreEntry[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      const { count } = await retryQuery(
        () =>
          supabase
            .from("toss_score_history" as any)
            .select("id", { count: "exact", head: true })
            .eq("token_symbol", symbol),
        { label: "toss_score_history:count" },
      );
      setHasHistory((count ?? 0) > 0);
    })();
  }, [symbol]);

  useEffect(() => {
    if (!open || entries !== null) return;
    setLoading(true);
    (async () => {
      const { data } = await retryQuery(
        () =>
          supabase
            .from("toss_score_history" as any)
            .select("id, score, base_score, social_boost, rating, calculated_at")
            .eq("token_symbol", symbol)
            .order("calculated_at", { ascending: false })
            .limit(8),
        { label: "toss_score_history:list" },
      );
      setEntries((data ?? []) as unknown as ScoreEntry[]);
      setLoading(false);
    })();
  }, [open, entries, symbol]);


  if (hasHistory === false) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary"
          aria-label="Score history"
        >
          <History className="w-3 h-3" />
          history
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold">
            <span className="font-mono">{symbol}</span> score history
          </h4>
          <Link to="/track-record" className="text-[11px] text-primary hover:underline">
            Ledger →
          </Link>
        </div>
        {loading || !entries ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">No recorded history yet.</p>
        ) : (
          <ol className="space-y-1.5">
            {entries.map((e, idx) => {
              const isLatest = idx === 0;
              return (
                <li key={e.id} className="flex items-baseline justify-between text-xs gap-2">
                  <span
                    className={`font-mono font-semibold ${
                      isLatest ? "text-foreground" : "line-through text-muted-foreground"
                    }`}
                  >
                    {e.score.toFixed(1)}
                    {e.social_boost > 0 && (
                      <span className="text-[9px] font-normal text-primary ml-1">
                        (+{e.social_boost.toFixed(0)} boost)
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(e.calculated_at).toLocaleDateString("en-AU", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ScoreHistoryInline;
